import { ChevronDown, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { CommentToggle } from '@/components/invoices/CommentToggle'
import { IncomingInvoiceControls } from '@/components/invoices/IncomingInvoiceControls'
import { InvoiceNumberCell } from '@/components/invoices/InvoiceNumberCell'
import { PayoutCommentsPanel } from '@/components/invoices/PayoutCommentsPanel'
import { PayoutWaitingLabel } from '@/components/invoices/PayoutWaitingLabel'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Money } from '@/components/ui/Money'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatCard } from '@/components/ui/StatCard'
import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useMyPayouts } from '@/lib/invoice-queries'
import type { MyPayoutLine } from '@/lib/invoice-types'
import { sortPayoutLines, waitingOn } from '@/lib/payout-waiting'
import { cn } from '@/lib/utils'

const COLS = 'grid-cols-[minmax(0,1fr)_8rem_16rem_12rem]'
const GRID = cn('grid', COLS, 'items-center gap-4 border-l-2 px-5')

export function MyPayoutsPage() {
  const payoutsQuery = useMyPayouts()
  const payouts = useMemo(() => payoutsQuery.data ?? [], [payoutsQuery.data])
  const [showHistory, setShowHistory] = useState(false)

  const openLines = useMemo(() => sortPayoutLines(payouts.filter((p) => p.payoutStatus === 'OPEN')), [payouts])
  const paidLines = useMemo(
    () =>
      payouts
        .filter((p) => p.payoutStatus === 'PAID')
        .sort((a, b) => (b.payoutPaidAt ?? '').localeCompare(a.payoutPaidAt ?? '')),
    [payouts],
  )

  const openTotal = openLines.reduce((s, p) => s + toNumber(p.amount), 0)
  const paidTotal = paidLines.reduce((s, p) => s + toNumber(p.amount), 0)

  const year = new Date().getFullYear()
  const paidThisYear = paidLines
    .filter((p) => p.payoutPaidAt?.startsWith(`${year}-`))
    .reduce((s, p) => s + toNumber(p.amount), 0)

  return (
    <div className="space-y-6">
      <PageHeader title="Mijn uitbetalingen" description="Wat jij zelf nog krijgt: jouw aandeel per factuur en de status van de uitbetaling." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          label="Openstaand"
          definition="Jouw aandeel dat nog niet is uitgekeerd, op facturen vanaf status Approved. De verdeling rekent altijd over het subtotaal, dus dit is excl. btw."
          icon={Wallet}
          loading={payoutsQuery.isLoading}
          numericValue={payoutsQuery.isLoading ? undefined : openTotal}
          formatValue={formatEuros}
        />
        <StatCard
          label={`Uitbetaald in ${year}`}
          definition="Jouw aandeel dat dit kalenderjaar is uitgekeerd, geteld op uitbetaaldatum (niet op factuurdatum). Een factuur van vorig jaar die dit jaar wordt uitbetaald telt dus hier mee, ook al hoorde die bij de omzet van vorig jaar. De verdeling rekent altijd over het subtotaal, dus dit is excl. btw."
          icon={Wallet}
          loading={payoutsQuery.isLoading}
          numericValue={payoutsQuery.isLoading ? undefined : paidThisYear}
          formatValue={formatEuros}
          delta={payoutsQuery.isLoading ? undefined : `sinds de start ${formatEuros(paidTotal)}`}
        />
      </div>
      <p className="text-xs text-ink-faint">Bedragen excl. btw</p>

      <div className="overflow-hidden rounded-card bg-surface shadow-card">
        <div className={cn(GRID, 'label-caps hidden border-b border-b-hairline border-l-transparent py-2 lg:grid')}>
          <span>Klant · Factuur</span>
          <Tooltip content="Jouw aandeel excl. btw. Het kleinere bedrag eronder (indien aanwezig) is het totaal van de ingediende inkoopfactuur, inclusief het eigen btw-tarief van de onderaannemer.">
            <span className="text-right">Bedrag</span>
          </Tooltip>
          <span>Wacht op</span>
          <span>Inkoopfactuur</span>
        </div>

        {payoutsQuery.isError ? (
          <ErrorState compact error={payoutsQuery.error} onRetry={() => payoutsQuery.refetch()} />
        ) : payoutsQuery.isLoading ? (
          <div className="divide-y divide-hairline">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={cn(GRID, 'border-l-transparent py-3.5')}>
                <Skeleton className="h-4 w-48" />
                <Skeleton className="ml-auto h-4 w-20" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : payouts.length === 0 ? (
          <EmptyState
            icon={Wallet}
            className="border-0 bg-transparent"
            title="Geen uitbetalingen"
            description="Zodra je een aandeel in een factuur hebt, verschijnt het hier."
          />
        ) : (
          <>
            {openLines.length === 0 ? (
              <p className="px-5 py-6 text-center text-sm text-ink-faint">Niets openstaand: alles is uitbetaald.</p>
            ) : (
              <div className="divide-y divide-hairline">
                {openLines.map((payout) => (
                  <PayoutRow key={payout.id} payout={payout} />
                ))}
              </div>
            )}

            {paidLines.length > 0 ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowHistory((v) => !v)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-hairline px-5 py-2.5 text-xs text-ink-faint transition-colors hover:bg-fill-1 hover:text-ink"
                >
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showHistory ? '' : '-rotate-90')} />
                  {showHistory ? 'Historie verbergen' : `Historie tonen (${paidLines.length} uitbetaald)`}
                </button>
                {showHistory ? (
                  <div className="divide-y divide-hairline border-t border-hairline">
                    {paidLines.map((payout) => (
                      <PayoutRow key={payout.id} payout={payout} muted />
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}

            <div
              className={cn(
                'flex items-center justify-between border-l-2 border-l-transparent border-t border-t-hairline-strong px-5 py-3',
                'lg:grid lg:items-center lg:gap-4',
                COLS,
              )}
            >
              <span className="text-xs text-ink-faint">
                {showHistory ? `${openLines.length} openstaand, ${paidLines.length} uitbetaald` : `${openLines.length} openstaand`}
              </span>
              <span className="money text-sm font-semibold text-ink lg:text-right">
                {formatEuros(showHistory ? openTotal + paidTotal : openTotal)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function PayoutRow({ payout, muted }: { payout: MyPayoutLine; muted?: boolean }) {
  const waiting = waitingOn(payout, 'self')
  const [threadOpen, setThreadOpen] = useState(waiting.state === 'declined')

  const controls = (
    <IncomingInvoiceControls
      lineId={payout.id}
      stage={payout.payoutStage}
      hasPdf={payout.hasIncomingInvoicePdf}
      filename={payout.incomingInvoiceFilename}
      incomingInvoiceNumber={payout.incomingInvoiceNumber}
      share={payout.amount}
      canManage={payout.canManageFile}
      canView={payout.canViewFile}
      autoSettle={payout.autoSettle}
    />
  )

  const identity = (
    <span className="min-w-0">
      <span className="block truncate font-medium text-ink">{payout.clientName}</span>
      <span className="block truncate text-sm text-ink-faint">{payout.subject || '-'}</span>
      <InvoiceNumberCell
        invoiceId={payout.invoiceId}
        invoiceNumber={payout.invoiceNumber}
        canViewInvoice={payout.canViewInvoice}
        variant="inline"
      />
    </span>
  )

  const thread = (
    <CommentToggle
      count={payout.commentCount}
      open={threadOpen}
      canComment={payout.canComment}
      onToggle={() => setThreadOpen((o) => !o)}
    />
  )

  return (
    <div className={cn('group', (muted || waiting.tone === 'muted') && 'opacity-60 transition-opacity hover:opacity-100')}>
      <div className={cn(GRID, waiting.border, 'hidden py-3.5 lg:grid')}>
        {identity}
        <Money
          amount={toNumber(payout.amount)}
          secondaryAmount={payout.incomingInvoiceTotal ? toNumber(payout.incomingInvoiceTotal) : null}
          emptySecondaryLabel="excl. btw"
          className="text-sm font-medium text-ink"
        />
        <PayoutWaitingLabel line={payout} viewer="self" />
        <span className="flex min-w-0 items-center justify-between gap-2">
          {controls}
          {thread}
        </span>
      </div>
      <div className={cn('flex flex-col gap-2 border-l-2 px-5 py-3 lg:hidden', waiting.border)}>
        <div className="flex items-center justify-between gap-3">
          {identity}
          <Money
            amount={toNumber(payout.amount)}
            secondaryAmount={payout.incomingInvoiceTotal ? toNumber(payout.incomingInvoiceTotal) : null}
            emptySecondaryLabel="excl. btw"
            className="text-sm font-medium text-ink"
          />
        </div>
        <PayoutWaitingLabel line={payout} viewer="self" />
        {controls}
        <span className="flex justify-end">{thread}</span>
      </div>

      {threadOpen && (
        <div className="px-5 pb-3">
          <PayoutCommentsPanel lineId={payout.id} canComment={payout.canComment} />
        </div>
      )}
    </div>
  )
}
